import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Alert } from '@detect/shared';
import { alertService } from '../../services/alerts';
import type { WithId } from '../../services/types';
import { useCursorList } from '../../hooks/useCursorList';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  LoadMoreButton,
} from '../../components/common';
import { formatRelativeTime } from '../../utils/formatters';

/**
 * Alert console (PRD §2.5). Filtered by ONE of status/severity (the
 * API's single-filter rule); defaults to open alerts, newest activity
 * first. Auto-refreshes: an alert console must not go stale silently.
 */

const REFRESH_MS = 30_000;

export function AlertsPage() {
  const [filter, setFilter] = useState('status:open');

  const fetchPage = useCallback(
    (tenantId: string, startAfter: string | undefined) => {
      const [field, value] = filter.split(':');
      return alertService.list(tenantId, {
        limit: 50,
        startAfter,
        ...(field === 'status' ? { status: value } : {}),
        ...(field === 'severity' ? { severity: value } : {}),
      });
    },
    [filter]
  );
  const list = useCursorList<WithId<Alert>>(fetchPage, filter);

  // Quiet background refresh of page 1 (cursor lists reload in place).
  useAutoRefresh(() => void list.reload(), REFRESH_MS);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Alerts</h1>
        <div className="page-actions">
          <label htmlFor="alert-filter" className="visually-hidden">
            Filter alerts
          </label>
          <select
            id="alert-filter"
            name="filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="status:open">Open</option>
            <option value="status:acknowledged">Acknowledged</option>
            <option value="status:resolved">Resolved</option>
            <option value="status:suppressed">Suppressed (SDT)</option>
            <option value="severity:critical">Critical (any status)</option>
            <option value="severity:error">Error (any status)</option>
            <option value="severity:warning">Warning (any status)</option>
          </select>
        </div>
      </div>

      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState title="Nothing here" description="No alerts match this filter." />
      ) : (
        <>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Resource</th>
                  <th>Datapoint</th>
                  <th>Status</th>
                  <th>Count</th>
                  <th>Last occurred</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span className={`severity-badge severity-${a.severity}`}>
                        {a.severity}
                      </span>
                    </td>
                    <td>
                      <Link to={`/alerts/${a.id}`}>{a.resourceName}</Link>
                    </td>
                    <td>
                      {a.sensorKey}/{a.datapoint}
                    </td>
                    <td>{a.status}</td>
                    <td>{a.occurrenceCount}</td>
                    <td>{formatRelativeTime(a.lastOccurredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <LoadMoreButton
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            loadMoreError={list.loadMoreError}
            onLoadMore={() => void list.loadMore()}
          />
        </>
      )}
    </div>
  );
}

function useAutoRefresh(callback: () => void, intervalMs: number): void {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  useEffect(() => {
    const id = window.setInterval(() => cbRef.current(), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
}
